/** The sign-in screen. AutoCAD has none; a database of other people's drawings needs one.
 *
 * ⚠⚠ **THIS REPLACED THE NGINX `allow`/`deny` AS THE ACCESS CONTROL, IT DID NOT REMOVE IT.**
 * `deploy/nginx-pdcad.conf` still refuses anything outside the office LAN and the tailnet. Two
 * layers, because the network rule cannot tell one designer from another and this cannot stop
 * someone who is already inside from reaching the port at all.
 *
 * ⚠ It looks like AutoCAD's own start screen rather than a web login, because it is the first
 * thing a designer sees every morning and a stock centred card would say "this is a website".
 */
import { useEffect, useRef, useState } from "react";

import { signIn, type Who } from "./api";

export default function Login({ onIn }: { onIn: (who: Who) => void }) {
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const first = useRef<HTMLInputElement | null>(null);

  useEffect(() => { first.current?.focus(); }, []);

  const go = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (busy || !user || !pass) return;
    setBusy(true);
    setErr(null);
    try {
      onIn(await signIn(user, pass));
    } catch (ex) {
      setErr(String(ex).replace(/^Error:\s*/, ""));
      // ⚠ Clear the PASSWORD and keep the username. Retyping a name you already got right is
      // the small insult every badly-built login commits.
      setPass("");
    } finally { setBusy(false); }
  };

  return (
    <div style={{ position: "absolute", inset: 0, background: "#2b2b2b", color: "#e8e8e8",
                  font: "13px/1.5 system-ui", display: "grid", placeItems: "center" }}>
      <form onSubmit={go}
            style={{ width: 340, background: "#3c3f41", border: "1px solid #555",
                     borderRadius: 4, boxShadow: "0 16px 50px rgba(0,0,0,.6)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px",
                      background: "#2f3133", borderBottom: "1px solid #555",
                      borderRadius: "4px 4px 0 0" }}>
          <span style={{ width: 26, height: 26, background: "#c0392b", color: "#fff",
                         display: "grid", placeItems: "center", borderRadius: 3,
                         font: "700 15px system-ui" }}>A</span>
          <div>
            <div style={{ font: "600 13px system-ui" }}>pdcad</div>
            <div style={{ fontSize: 11, color: "#9aa0a6" }}>Drafting &amp; Annotation</div>
          </div>
        </div>
        <div style={{ padding: 16, display: "grid", gap: 10 }}>
          <label style={{ display: "grid", gap: 4 }}>
            <span style={{ fontSize: 11, color: "#9aa0a6" }}>User name</span>
            <input ref={first} value={user} onChange={(e) => setUser(e.target.value)}
                   autoComplete="username" data-testid="login-user" style={inp} />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span style={{ fontSize: 11, color: "#9aa0a6" }}>Password</span>
            <input type="password" value={pass} onChange={(e) => setPass(e.target.value)}
                   autoComplete="current-password" data-testid="login-pass" style={inp} />
          </label>
          {/* ⚠ The server's own sentence. It deliberately does not say WHICH half was wrong —
              "no such user" tells whoever is guessing that the other name they tried exists. */}
          {err && <div data-testid="login-error"
                       style={{ color: "#ff8f8f", fontSize: 11.5 }}>{err}</div>}
          {/* ⚠ A DISABLED BUTTON MUST LOOK DISABLED. The first version kept full colour while
              `disabled` was true, so an empty form showed a bright blue "Sign in" that swallowed
              every click in silence — which is this app's own rule about grey buttons, broken on
              the very first screen anyone sees. */}
          <button type="submit" disabled={busy || !user || !pass} data-testid="login-go"
                  style={{ marginTop: 4,
                           background: busy || !user || !pass ? "#3a4450" : "#3c5a82",
                           border: `1px solid ${busy || !user || !pass ? "#4a5563" : "#4a6d9c"}`,
                           color: busy || !user || !pass ? "#8d96a0" : "#fff", padding: "6px 0",
                           borderRadius: 3,
                           cursor: busy || !user || !pass ? "not-allowed" : "pointer",
                           font: "600 12px system-ui" }}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          {/* ⚠ Says where the drawings are. A designer who signs in on the wrong machine and
              finds an empty file list has no way to tell what went wrong. */}
          <div style={{ fontSize: 10.5, color: "#7a7e82", textAlign: "center", marginTop: 2 }}>
            {window.location.host}
          </div>
        </div>
      </form>
    </div>
  );
}

const inp: React.CSSProperties = {
  background: "#2b2d2f", border: "1px solid #555", color: "#e8e8e8",
  padding: "5px 8px", borderRadius: 2, font: "12.5px system-ui",
};
